import apiClient from '../client'

export interface EmailTemplate {
  id: number
  template_type: string
  language: string
  version: string
  name: string
  description: string | null
  subject: string
  body_html: string
  body_text: string | null
  variables_schema: Record<string, unknown> | null
  is_active: boolean
  is_default: boolean
  times_used: number
  last_used_at: string | null
  created_by: string | null
  updated_by: string | null
  created_at: string
  updated_at: string
}

export interface EmailTemplateListResponse {
  templates: EmailTemplate[]
  total: number
}

export interface TemplateType {
  id: string
  name: string
  description: string
}

export interface TemplateLanguage {
  code: string
  name: string
}

export interface TemplateTypesResponse {
  template_types: TemplateType[]
  languages: TemplateLanguage[]
  variables: Record<string, string>
}

export interface CreateEmailTemplateData {
  template_type: string
  language: string
  name: string
  description?: string
  subject: string
  body_html: string
  body_text?: string
  variables_schema?: Record<string, unknown>
  is_default?: boolean
  created_by?: string
}

export interface UpdateEmailTemplateData {
  name?: string
  description?: string
  subject?: string
  body_html?: string
  body_text?: string
  variables_schema?: Record<string, unknown>
  is_active?: boolean
  is_default?: boolean
  updated_by?: string
}

export interface PreviewRequest {
  body_html: string
  variables: Record<string, string>
}

export interface PreviewResponse {
  rendered_html: string
  rendered_subject?: string
}

export interface AIAnalysisRequest {
  subject: string
  body_html: string
  analysis_type: 'spam_check' | 'improve' | 'full'
}

export interface AIAnalysisResponse {
  spam_score: number
  spam_triggers: string[]
  subject_suggestions: string[]
  content_suggestions: string[]
  improved_subject: string | null
  improved_body_html: string | null
  deliverability_tips: string[]
  analysis_summary: string
}

export interface EmailTemplateVersion {
  id: number
  template_id: number
  version: string
  version_number: number
  name: string
  description: string | null
  subject: string
  body_html: string
  body_text: string | null
  variables_schema: Record<string, unknown> | null
  change_summary: string | null
  changed_by: string | null
  created_at: string
}

export interface EmailTemplateVersionListResponse {
  versions: EmailTemplateVersion[]
  total: number
  current_version: string
}

export const emailTemplatesApi = {
  // List all templates
  getAll: (params?: { template_type?: string; language?: string; active_only?: boolean }) =>
    apiClient.get<EmailTemplateListResponse>('/api/email-templates', { params }),

  // Get template types and languages
  getTypes: () =>
    apiClient.get<TemplateTypesResponse>('/api/email-templates/types'),

  // Get single template
  getById: (id: number) =>
    apiClient.get<EmailTemplate>(`/api/email-templates/${id}`),

  // Get default template for type/language
  getDefault: (templateType: string, language: string) =>
    apiClient.get<EmailTemplate>(`/api/email-templates/default/${templateType}/${language}`),

  // Create new template
  create: (data: CreateEmailTemplateData) =>
    apiClient.post<{ success: boolean; template: EmailTemplate }>('/api/email-templates', data),

  // Update template
  update: (id: number, data: UpdateEmailTemplateData) =>
    apiClient.put<{ success: boolean; template: EmailTemplate }>(`/api/email-templates/${id}`, data),

  // Delete template
  delete: (id: number, hardDelete?: boolean) =>
    apiClient.delete<{ success: boolean; message: string }>(`/api/email-templates/${id}`, {
      params: hardDelete ? { hard_delete: true } : undefined
    }),

  // Set as default
  setDefault: (id: number) =>
    apiClient.post<{ success: boolean; message: string }>(`/api/email-templates/${id}/set-default`),

  // Preview template
  preview: (data: PreviewRequest) =>
    apiClient.post<PreviewResponse>('/api/email-templates/preview', data),

  // Duplicate template
  duplicate: (id: number, targetLanguage?: string) =>
    apiClient.post<{ success: boolean; template: EmailTemplate }>(
      `/api/email-templates/${id}/duplicate`,
      null,
      { params: targetLanguage ? { target_language: targetLanguage } : undefined }
    ),

  // Run migration (create table)
  migrate: () =>
    apiClient.post<{ success: boolean; message: string; results: string[] }>('/api/email-templates/migrate'),

  // Seed default templates
  seed: () =>
    apiClient.post<{ success: boolean; message: string; results: string[] }>('/api/email-templates/seed'),

  // AI Analysis - analyze template for spam and improvements
  analyzeWithAI: (data: AIAnalysisRequest) =>
    apiClient.post<AIAnalysisResponse>('/api/email-templates/ai/analyze', data),

  // Version History
  getVersions: (id: number) =>
    apiClient.get<EmailTemplateVersionListResponse>(`/api/email-templates/${id}/versions`),

  getVersion: (templateId: number, versionId: number) =>
    apiClient.get<EmailTemplateVersion>(`/api/email-templates/${templateId}/versions/${versionId}`),

  revertToVersion: (templateId: number, versionId: number) =>
    apiClient.post<{ success: boolean; message: string; template: EmailTemplate }>(
      `/api/email-templates/${templateId}/revert/${versionId}`
    ),
}
